/* ReportsAdvanced — Laporan Penjualan Lengkap (ENTERPRISE, feature: reports.sales_advanced).
   Summary metrics + period comparison, trends chart (recharts), rankings, export CSV. */
import React, { useCallback, useEffect, useState } from "react";
import { Download, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import api, { formatRp, notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const PRESETS = [
  { key: "today", label: "Hari ini" },
  { key: "7d", label: "7 hari terakhir" },
  { key: "30d", label: "30 hari terakhir" },
  { key: "this_month", label: "Bulan ini" },
  { key: "last_month", label: "Bulan lalu" },
  { key: "this_year", label: "Tahun ini" },
];

const STATUS_LABEL = {
  pending_payment: "Belum Dibayar", paid: "Dibayar", processing: "Diproses",
  shipped: "Dikirim", completed: "Selesai", cancelled: "Dibatalkan",
};

function presetRange(key) {
  const now = new Date();
  const d = (dt) => dt.toISOString().slice(0, 10);
  if (key === "today") return [d(now), d(now)];
  if (key === "7d") return [d(new Date(now - 6 * 864e5)), d(now)];
  if (key === "30d") return [d(new Date(now - 29 * 864e5)), d(now)];
  if (key === "this_month") return [d(new Date(now.getFullYear(), now.getMonth(), 1)), d(now)];
  if (key === "last_month") {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    return [d(s), d(e)];
  }
  if (key === "this_year") return [d(new Date(now.getFullYear(), 0, 1)), d(now)];
  return null;
}

function ChangeBadge({ pct, testId }) {
  if (pct === null || pct === undefined) {
    return <span className="text-[10px] text-[#5C5C5C]" data-testid={testId}>Belum ada data pembanding</span>;
  }
  const up = pct > 0, flat = pct === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${flat ? "text-[#5C5C5C]" : up ? "text-[#2D6A4F]" : "text-[#DC2F02]"}`} data-testid={testId}>
      <Icon size={12} /> {up ? "+" : ""}{pct}% vs periode sebelumnya
    </span>
  );
}

function MetricCard({ label, value, badge, testId }) {
  return (
    <Card className="p-4 rounded-2xl" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">{label}</div>
      <div className="font-display text-xl font-black mt-1">{value}</div>
      {badge}
    </Card>
  );
}

function RankTable({ title, headers, rows, empty, testId }) {
  return (
    <Card className="rounded-2xl overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b font-bold text-sm">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b">
              {headers.map((h) => <th key={h} className="px-4 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length} className="px-4 py-6 text-center text-xs text-[#5C5C5C]">{empty}</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                {r.map((c, j) => <td key={j} className={`px-4 py-2 ${j > 0 ? "text-xs" : "text-xs font-semibold"}`}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function ReportsAdvanced({ start, end, setStart, setEnd }) {
  const [filters, setFilters] = useState({
    status: "", payment_status: "", payment_method: "", product_id: "", category_id: "", customer_id: "",
  });
  const [options, setOptions] = useState({ products: [], categories: [], customers: [] });
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [granularity, setGranularity] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/products", { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
      api.get("/categories").catch(() => ({ data: [] })),
      api.get("/admin/customers", { params: { limit: 100 } }).catch(() => ({ data: [] })),
    ]).then(([p, c, u]) => setOptions({
      products: p.data.items || p.data || [],
      categories: c.data || [],
      customers: Array.isArray(u.data) ? u.data : u.data.items || [],
    }));
  }, []);

  const params = useCallback(() => {
    const p = { start: new Date(start).toISOString(), end: new Date(end + "T23:59:59").toISOString() };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [start, end, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = params();
      const [s, t, r] = await Promise.all([
        api.get("/admin/reports/sales/summary", { params: p }),
        api.get("/admin/reports/sales/trends", { params: { ...p, granularity } }),
        api.get("/admin/reports/sales/rankings", { params: p }),
      ]);
      setSummary(s.data); setTrends(t.data); setRankings(r.data);
    } catch (e) { notifyError(e); }
    setLoading(false);
  }, [params, granularity]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/reports/sales/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan-lengkap-${start}-${end}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export berhasil diunduh.");
    } catch (e) { notifyError(e); }
    setExporting(false);
  };

  const applyPreset = (key) => {
    const r = presetRange(key);
    if (r) { setStart(r[0]); setEnd(r[1]); }
  };

  const updF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const selCls = "h-10 rounded-xl border border-input bg-background px-2 text-xs w-full";
  const m = summary?.metrics;
  const cmp = summary?.comparison;

  return (
    <div className="space-y-4" data-testid="reports-advanced">
      {/* Filters */}
      <Card className="p-4 rounded-2xl space-y-3" data-testid="adv-filters">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`adv-preset-${p.key}`}
              className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#E5E5E5] hover:brand-soft-bg">
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 items-end">
          <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl h-10" data-testid="adv-start" /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl h-10" data-testid="adv-end" /></div>
          <div><Label className="text-xs">Status Pesanan</Label>
            <select value={filters.status} onChange={(e) => updF("status", e.target.value)} className={selCls} data-testid="adv-filter-status">
              <option value="">Semua</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Status Pembayaran</Label>
            <select value={filters.payment_status} onChange={(e) => updF("payment_status", e.target.value)} className={selCls} data-testid="adv-filter-payment-status">
              <option value="">Semua</option>
              <option value="pending_verification">Menunggu Verifikasi</option>
              <option value="verified">Terverifikasi</option>
              <option value="paid">Dibayar</option>
              <option value="expired">Kedaluwarsa</option>
            </select>
          </div>
          <div><Label className="text-xs">Metode Pembayaran</Label>
            <select value={filters.payment_method} onChange={(e) => updF("payment_method", e.target.value)} className={selCls} data-testid="adv-filter-payment-method">
              <option value="">Semua</option>
              <option value="qris">QRIS</option>
              <option value="bca">Transfer BCA</option>
              <option value="midtrans">Midtrans</option>
            </select>
          </div>
          <div><Label className="text-xs">Produk</Label>
            <select value={filters.product_id} onChange={(e) => updF("product_id", e.target.value)} className={selCls} data-testid="adv-filter-product">
              <option value="">Semua</option>
              {options.products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Kategori</Label>
            <select value={filters.category_id} onChange={(e) => updF("category_id", e.target.value)} className={selCls} data-testid="adv-filter-category">
              <option value="">Semua</option>
              {options.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><Label className="text-xs">Pelanggan</Label>
            <select value={filters.customer_id} onChange={(e) => updF("customer_id", e.target.value)} className={selCls} data-testid="adv-filter-customer">
              <option value="">Semua</option>
              {options.customers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={exportCsv} disabled={exporting || loading} variant="outline" className="rounded-xl" data-testid="adv-export-btn">
            <Download size={14} /> {exporting ? "Mengunduh…" : "Export CSV"}
          </Button>
        </div>
      </Card>

      {loading && !summary ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-24 rounded-2xl bg-black/5 animate-pulse" />)}
        </div>
      ) : m && (
        <>
          {/* Summary metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="adv-summary">
            <MetricCard label="Total Omzet" value={formatRp(m.total_revenue)} testId="adv-metric-revenue"
              badge={<ChangeBadge pct={cmp?.revenue_change_pct} testId="adv-cmp-revenue" />} />
            <MetricCard label="Total Pesanan (Paid)" value={m.paid_orders} testId="adv-metric-orders"
              badge={<ChangeBadge pct={cmp?.orders_change_pct} testId="adv-cmp-orders" />} />
            <MetricCard label="Rata-rata Nilai Pesanan" value={formatRp(m.avg_order_value)} testId="adv-metric-aov"
              badge={<ChangeBadge pct={cmp?.aov_change_pct} testId="adv-cmp-aov" />} />
            <MetricCard label="Produk Terjual" value={m.items_sold} testId="adv-metric-items" />
            <MetricCard label="Total Diskon" value={formatRp(m.total_discount)} testId="adv-metric-discount" />
            <MetricCard label="Total Ongkir" value={formatRp(m.total_shipping)} testId="adv-metric-shipping" />
            <MetricCard label="Order Berhasil / Batal" value={`${m.paid_orders} / ${m.cancelled_orders}`} testId="adv-metric-success-cancel" />
            <MetricCard label="Bayar Pending / Berhasil" value={`${m.payment_pending} / ${m.payment_success}`} testId="adv-metric-payments" />
          </div>

          {/* Trends chart */}
          <Card className="p-4 rounded-2xl" data-testid="adv-chart">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-bold text-sm">Tren Penjualan (Omzet & Order)</h3>
              <div className="flex gap-1">
                {[["daily", "Harian"], ["weekly", "Mingguan"], ["monthly", "Bulanan"]].map(([k, v]) => (
                  <button key={k} onClick={() => setGranularity(k)} data-testid={`adv-granularity-${k}`}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full ${granularity === k ? "brand-bg-primary text-white" : "border border-[#E5E5E5]"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
            {trends?.points?.length ? (
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trends.points} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="rev" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <YAxis yAxisId="ord" orientation="right" allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v, name) => name === "Omzet" ? [formatRp(v), name] : [v, name]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="ord" dataKey="orders" name="Order" fill="#F9C846" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="rev" dataKey="revenue" name="Omzet" stroke="#E85D04" strokeWidth={2.5} dot={{ r: 2 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-40 grid place-items-center text-xs text-[#5C5C5C]" data-testid="adv-chart-empty">
                Belum ada data penjualan pada periode ini.
              </div>
            )}
          </Card>

          {/* Rankings */}
          {rankings && (
            <div className="grid lg:grid-cols-2 gap-4" data-testid="adv-rankings">
              <RankTable title="Produk Terlaris" headers={["Produk", "Qty", "Omzet"]} testId="adv-rank-products"
                empty="Belum ada data" rows={rankings.products.map((p) => [p.name, p.quantity, formatRp(p.revenue)])} />
              <RankTable title="Kategori Terlaris" headers={["Kategori", "Qty", "Omzet"]} testId="adv-rank-categories"
                empty="Belum ada data" rows={rankings.categories.map((c) => [c.name, c.quantity, formatRp(c.revenue)])} />
              <RankTable title="Pelanggan Teratas" headers={["Pelanggan", "Order", "Total Belanja"]} testId="adv-rank-customers"
                empty="Belum ada data" rows={rankings.customers.map((c) => [c.name, c.orders, formatRp(c.total)])} />
              <RankTable title="Metode Pembayaran" headers={["Metode", "Transaksi", "Total Nilai"]} testId="adv-rank-payments"
                empty="Belum ada data" rows={rankings.payment_methods.map((p) => [String(p.method).toUpperCase(), p.count, formatRp(p.total)])} />
              <RankTable title="Status Pesanan" headers={["Status", "Jumlah"]} testId="adv-rank-status"
                empty="Belum ada data" rows={rankings.status_distribution.map((s) => [STATUS_LABEL[s.status] || s.status, s.count])} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
